import { useRef, useEffect, useCallback } from "react";
import {
	setBgmVolume,
	getBgmElement,
	getActiveBgmSystem,
	getBgmAudio,
	pauseBgm,
	resumeBgm,
	isBgmPlaying,
	isCurrentPathBgmRoute,
} from "../utils/audioManager";

/**
 * useBgmDucking - Hook for managing background music ducking
 * Handles ducking and restoring BGM volume for recording scenarios
 */
export const useBgmDucking = () => {
	const previousBgmVolumeRef = useRef(null);

	// Duck BGM to a specific volume level
	const duckBgm = useCallback((targetVolume = 0.03) => {
		try {
			const activeSystem = getActiveBgmSystem();

			if (activeSystem === "html5") {
				const htmlBgm = getBgmElement();
				if (htmlBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = htmlBgm.volume || 0.5;
					setBgmVolume(targetVolume);
				}
			} else if (activeSystem === "web") {
				const webBgm = getBgmAudio();
				if (webBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = webBgm.volume || 0.5;
					setBgmVolume(targetVolume);
				}
			}
		} catch (err) {
			console.warn("useBgmDucking: Failed to duck BGM:", err);
		}
	}, []);

	// Restore BGM to previous volume, resuming if iOS audio session interrupted it
	const restoreBgm = useCallback(() => {
		if (previousBgmVolumeRef.current === null) {
			return;
		}
		try {
			const previousVolume = previousBgmVolumeRef.current;
			previousBgmVolumeRef.current = null;

			if (!isCurrentPathBgmRoute()) {
				// Navigated away from a BGM page — don't resume
				return;
			}
			if (!isBgmPlaying()) {
				// iOS audio session (camera/mic) may have paused the BGM element — resume it
				resumeBgm()
					.then(() => setBgmVolume(previousVolume))
					.catch((err) =>
						console.warn("useBgmDucking: Failed to resume BGM:", err)
					);
			} else {
				setBgmVolume(previousVolume);
			}
		} catch (err) {
			console.warn("useBgmDucking: Failed to restore BGM:", err);
		}
	}, []);

	// Pause BGM for audio recording (prevents mic bleed); restoreBgm resumes it
	const pauseBgmForCapture = useCallback(() => {
		try {
			const activeSystem = getActiveBgmSystem();
			if (activeSystem === "html5") {
				const htmlBgm = getBgmElement();
				if (htmlBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = htmlBgm.volume || 0.5;
				}
			} else if (activeSystem === "web") {
				const webBgm = getBgmAudio();
				if (webBgm && previousBgmVolumeRef.current === null) {
					previousBgmVolumeRef.current = webBgm.volume || 0.5;
				}
			}
			pauseBgm();
		} catch (err) {
			console.warn("useBgmDucking: Failed to pause BGM for capture:", err);
		}
	}, []);

	// Duck BGM when mode changes to audio
	const duckBgmForMode = useCallback(
		(mode) => {
			if (mode === "audio") {
				pauseBgmForCapture(); // Full pause for audio recording to prevent mic bleed
			}
		},
		[pauseBgmForCapture]
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			restoreBgm();
		};
	}, [restoreBgm]);

	return {
		duckBgm,
		restoreBgm,
		duckBgmForMode,
		pauseBgmForCapture,
	};
};

export default useBgmDucking;
